<template>
  <div>
    <b-modal ref="newMaskModal"
             :title="edit ? $t('evaluation.mask.edit') : $t('evaluation.mask.new')"
             :ok-title="$t('save')"
             :cancel-title="$t('cancel')"
             size="lg"
             centered
             @ok.prevent="save()"
             @hide="onModalHide"
             @shown="onModalShown"
             @hidden="onModalHidden">
      <form @submit.prevent="save()" @keydown="form.onKeydown($event)">
        <div class="form-group">
          <label for="maskname" class="col-form-label">{{ $t('evaluation.mask.name') }} :</label>
          <input id="maskname" v-model="form.title" type="text" class="form-control" maxlength="100">
          <form-error :form="form" field="title" />
        </div>
      </form>
      <div class="card">
        <div class="card-body">
          <div class="mask-tree-view">
            <CheckableTreeView v-if="maskData" ref="maskTreeView" :data="maskData" />
          </div>
        </div>
      </div>
      <div v-if="showCounter" class="text-right">
        {{ $t('evaluation.mask.information', [$refs.maskTreeView.state.checked, $refs.maskTreeView.state.children]) }}
      </div>
      <div slot="modal-footer" class="w-100">
        <div class="d-flex justify-content-between align-items-center flex-wrap">
          <div class="d-flex align-items-center flex-wrap">
            <Checkbox v-model="form.public" class="with-buttons-on-right">{{ $t('evaluation.mask.submit') }}</Checkbox>
            <small v-if="localDraftSavedAtLabel" class="text-muted ml-3">
              {{ $t('manage.masks.local_draft_saved_at', [localDraftSavedAtLabel]) }}
            </small>
          </div>
          <div class="float-right">
            <b-btn variant="secondary" @click="hide()">{{ $t('cancel') }}</b-btn>
            <b-btn variant="primary" @click="save()">{{ $t('save') }}</b-btn>
          </div>
        </div>
        <div v-if="hasStoredDraft" class="mt-2 text-left">
          <small class="text-warning">{{ $t('manage.masks.local_draft_available') }}</small>
          <b-btn size="sm" variant="outline-primary" class="ml-2" @click="restoreLocalDraft()">{{ $t('manage.masks.restore_local_draft') }}</b-btn>
          <b-btn size="sm" variant="outline-danger" class="ml-1" @click="resetLocalDraft()">{{ $t('manage.masks.reset_local_draft') }}</b-btn>
        </div>
      </div>
    </b-modal>
    <CallbackModal ref="unsavedChangesModal"
                   :title="$t('unsaved_modifications')"
                   :message="$t('evaluation.unsaved_modifications')" />
    <CallbackModal ref="restoreLocalDraftModal"
                   :title="$t('manage.masks.local_draft_restore_title')"
                   :message="localDraftRestoreMessage" />
  </div>
</template>

<script>
import Vue from 'vue'
import Form from 'vform'
import { mapGetters } from 'vuex'
const LOCAL_DRAFT_VERSION = 1
const LOCAL_DRAFT_AUTOSAVE_MS = 2000

export default {
  name: 'MaskModal',

  data: () => ({
    maskData: null,
    form: new Form({
      mask: '',
      creator_id: null,
      organization_id: null,
      public: false,
      title: '' // new Date(Date.now()).toDateString(),
    }),
    edit: false,
    showCounter: false,
    mask_id: 0,
    initialSnapshot: null,
    initialSnapshotSignature: null,
    allowUnsafeClose: false,
    isModalVisible: false,
    autosaveIntervalId: null,
    hasStoredDraft: false,
    localDraftSavedAtLabel: ''
  }),

  computed: {
    ...mapGetters({
      abilities: 'evaluations/abilities',
      user: 'auth/user'
    }),
    localDraftRestoreMessage () {
      if (this.localDraftSavedAtLabel) {
        return this.$t('manage.masks.local_draft_restore_message', [this.localDraftSavedAtLabel])
      }

      return this.$t('manage.masks.local_draft_restore_message_no_date')
    }
  },

  mounted () {
    this.resetModal()
  },

  beforeDestroy () {
    this.stopAutosave()
  },

  methods: {
    hide (force = false) {
      if (force) {
        this.forceCloseModal()
        return
      }

      if (this.hasUnsavedChanges()) {
        this.confirmDiscardChanges(() => {
          this.forceCloseModal()
        })
        return
      }

      this.$refs.newMaskModal.hide()
    },

    forceCloseModal () {
      this.allowUnsafeClose = true
      this.$refs.newMaskModal.hide()
    },

    $_load (mask, index, data) {
      index.value++

      for (let i = 0; i < data.length; i++) {
        let node = data[i]
        let masked = mask.mask[index.value] === '0'

        Vue.set(node, 'masked', masked)

        if (masked) {
          index.value++
          continue
        } else {
          this.$_load(mask, index, node.children)
        }
      }
    },
    isTreeReady () {
      return this.abilities && this.abilities.tree && this.abilities.tree.length > 0
    },

    cloneAbilityTree () {
      if (!this.isTreeReady()) {
        return null
      }

      return JSON.parse(JSON.stringify(this.abilities.tree))[0]
    },

    applyMaskToTree (mask, data) {
      if (!mask.mask || mask.mask.length === 0) {
        return
      }

      Vue.set(data[0], 'masked', mask.mask[0] === '0')

      const index = { value: 0 }
      this.$_load(mask, index, data[0].children)
    },

    load (mask, data) {
      this.mask_id = mask.id
      this.form.mask = mask.mask
      this.form.creator_id = mask.creator_id
      this.form.organization_id = mask.organization_id
      this.form.public = mask.public
      this.form.title = mask.title
      this.form.public = !!mask.public
      this.applyMaskToTree(mask, data)
    },

    applySnapshot (snapshot) {
      if (!snapshot) {
        return
      }
      const baseTree = this.cloneAbilityTree()
      if (!baseTree) {
        return
      }

      this.form.title = snapshot.title || ''
      this.form.public = !!snapshot.public
      this.form.mask = snapshot.mask || ''
      const tmpData = [this.initTreeViewData(baseTree)]
      this.applyMaskToTree({ mask: snapshot.mask || '' }, tmpData)

      if (!this.maskData) {
        this.maskData = tmpData
      } else {
        Vue.set(this.maskData, 0, tmpData[0])
      }

      this.$nextTick(() => {
        if (this.$refs.maskTreeView) {
          this.$refs.maskTreeView.refresh()
        }
      })
    },

    resetModal () {
      const baseTree = this.cloneAbilityTree()
      if (!baseTree) {
        return
      }
      this.form.reset()
      this.maskData = [this.initTreeViewData(baseTree)]
      this.mask_id = 0
      this.form.title = new Date(Date.now()).toUserLocaleString(this)
      this.form.public = false
      this.form.creator_id = this.user ? this.user.id : null
      this.form.organization_id = this.user ? this.user.organization_id : null
      this.setEdit(false)
      if (this.$refs.maskTreeView) {
        this.$refs.maskTreeView.refresh()
      }
      this.loadStoredDraftMetadata()
    },

    new () {
      this.resetModal()
      this.show()
    },

    editMask (mask, edit = true) {
      const baseTree = this.cloneAbilityTree()
      if (!baseTree) {
        return
      }

      var tmpData = [this.initTreeViewData(baseTree)]
      this.setEdit(edit)
      this.load(mask, tmpData)

      if (!edit) { this.form.title = '' }

      if (!this.maskData) { this.maskData = tmpData } else { Vue.set(this.maskData, 0, tmpData[0]) }

      if (this.$refs.maskTreeView) { this.$refs.maskTreeView.refresh() }
      this.loadStoredDraftMetadata()
      this.show()
    },

    initTreeViewData (data) {
      data.masked = true
      if (data.children.length > 0) {
        for (var i = 0; i < data.children.length; i++) { data.children[i] = this.initTreeViewData(data.children[i]) }
      }
      return data
    },
    getDraftMode () {
      return this.edit ? 'edit' : 'new'
    },

    getDraftMaskIdentifier () {
      return this.edit ? this.mask_id : 'new'
    },

    getDraftStorageKey (mode = this.getDraftMode(), maskId = this.getDraftMaskIdentifier()) {
      const userId = this.user && this.user.id ? this.user.id : 'unknown'
      return `esap.mask.draft.v${LOCAL_DRAFT_VERSION}.user.${userId}.${mode}.${maskId}`
    },

    formatDraftDate (value) {
      if (!value) {
        return ''
      }

      return new Date(value).toUserLocaleString(this)
    },

    getCurrentMaskString () {
      if (this.$refs.maskTreeView && this.$refs.maskTreeView.getMask) {
        return this.$refs.maskTreeView.getMask().join('')
      }

      return this.form.mask || ''
    },

    getSnapshot () {
      return {
        title: this.form.title || '',
        public: !!this.form.public,
        mask: this.getCurrentMaskString(),
        edit: !!this.edit,
        mask_id: this.edit ? this.mask_id : null
      }
    },

    getSnapshotSignature (snapshot = this.getSnapshot()) {
      return JSON.stringify(snapshot)
    },

    captureInitialSnapshot () {
      this.initialSnapshot = this.getSnapshot()
      this.initialSnapshotSignature = this.getSnapshotSignature(this.initialSnapshot)
    },

    hasUnsavedChanges () {
      if (!this.isModalVisible || !this.initialSnapshotSignature) {
        return false
      }

      return this.getSnapshotSignature() !== this.initialSnapshotSignature
    },

    hasPendingChanges () {
      return this.hasUnsavedChanges()
    },

    confirmLeaveWithUnsavedChanges (callback) {
      this.confirmDiscardChanges(callback)
    },

    readLocalDraft () {
      try {
        const rawDraft = localStorage.getItem(this.getDraftStorageKey())
        return rawDraft ? JSON.parse(rawDraft) : null
      } catch (error) {
        return null
      }
    },

    updateLocalDraftIndicator (draft = null) {
      this.hasStoredDraft = !!draft
      this.localDraftSavedAtLabel = draft && draft.saved_at ? this.formatDraftDate(draft.saved_at) : ''
    },

    loadStoredDraftMetadata () {
      this.updateLocalDraftIndicator(this.readLocalDraft())
    },

    saveLocalDraft (force = false) {
      if (!this.isModalVisible && !force) {
        return
      }

      if (!force && !this.hasUnsavedChanges()) {
        return
      }

      const snapshot = this.getSnapshot()
      if (!snapshot.mask || snapshot.mask.length === 0) {
        return
      }

      const draft = {
        ...snapshot,
        saved_at: new Date().toISOString()
      }

      try {
        localStorage.setItem(this.getDraftStorageKey(), JSON.stringify(draft))
        this.updateLocalDraftIndicator(draft)
      } catch (error) {
        console.error(error)
      }
    },

    clearLocalDraft () {
      try {
        localStorage.removeItem(this.getDraftStorageKey())
      } catch (error) {
        console.error(error)
      }

      this.updateLocalDraftIndicator()
    },

    resetLocalDraft () {
      this.clearLocalDraft()
      this.applySnapshot(this.initialSnapshot)
    },

    restoreLocalDraft (draft = null) {
      const localDraft = draft || this.readLocalDraft()
      if (!localDraft) {
        return
      }

      this.applySnapshot(localDraft)
      this.updateLocalDraftIndicator(localDraft)
    },

    async save () {
      // Populate form.
      let mask = this.getCurrentMaskString()

      if (this.edit) {
        this.form.creator_id = this.form.creator_id || this.user.id
      } else {
        this.form.creator_id = this.user.id
      }
      this.form.organization_id = this.user.organization_id
      this.form.mask = mask

      // Send form.

      try {
        let data = null
        if (this.edit) {
          ({ data } = await this.form.patch('/api/evaluations/masks/' + this.mask_id))
        } else {
          ({ data } = await this.form.post('/api/evaluations/masks'))
        }

        // Hide and reset modal.
        this.clearLocalDraft()
        this.captureInitialSnapshot()
        this.hide(true)

        this.$emit('saved', data)
      } catch (e) {
        this.saveLocalDraft(true)
        console.error(e)
      }
    },

    show () {
      this.$refs.newMaskModal.show()
      // YGO to self or next maintainer
      // The following is uber mega giga ugly, but works... Sorry for that.
      // There is a bug when the criteria counter in the mask modal is not showing
      // unless we type something in the name field
      // There must be a broken link somewhere between the components,...
      // After searching for hours I did this monstruosity.
      // Will commit separately
      setTimeout(() => {
        this.showCounter = true
      })
    },

    setEdit (state) {
      this.edit = state
    },

    confirmDiscardChanges (callback) {
      if (!this.hasUnsavedChanges()) {
        if (callback) {
          callback()
        }
        return
      }

      this.$refs.unsavedChangesModal.show(() => {
        if (callback) {
          callback()
        }
      })
    },

    onModalHide (event) {
      if (this.allowUnsafeClose) {
        this.allowUnsafeClose = false
        return
      }

      if (this.hasUnsavedChanges()) {
        event.preventDefault()
        this.confirmDiscardChanges(() => {
          this.forceCloseModal()
        })
      }
    },

    onModalShown () {
      this.isModalVisible = true
      this.captureInitialSnapshot()
      this.loadStoredDraftMetadata()
      this.startAutosave()
      this.promptLocalDraftRestoreIfNeeded()
    },

    onModalHidden () {
      this.isModalVisible = false
      this.showCounter = false
      this.stopAutosave()
      this.$emit('local-draft-updated')
    },

    startAutosave () {
      this.stopAutosave()
      this.autosaveIntervalId = setInterval(() => {
        this.saveLocalDraft()
      }, LOCAL_DRAFT_AUTOSAVE_MS)
    },

    stopAutosave () {
      if (this.autosaveIntervalId) {
        clearInterval(this.autosaveIntervalId)
        this.autosaveIntervalId = null
      }
    },

    shouldPromptLocalDraftRestore (draft) {
      if (!draft) {
        return false
      }

      const comparableDraft = {
        title: draft.title || '',
        public: !!draft.public,
        mask: draft.mask || '',
        edit: !!this.edit,
        mask_id: this.edit ? this.mask_id : null
      }

      return this.getSnapshotSignature(comparableDraft) !== this.getSnapshotSignature()
    },

    promptLocalDraftRestoreIfNeeded () {
      const draft = this.readLocalDraft()
      if (!this.shouldPromptLocalDraftRestore(draft)) {
        return
      }

      this.$refs.restoreLocalDraftModal.show(() => {
        this.restoreLocalDraft(draft)
      })
    }
  }
}
</script>

<style scoped>
.mask-tree-view {
  max-height: 500px;
  overflow-y: auto  ;
}
</style>



// WEBPACK FOOTER //
// ../../../../resources/js/components/MaskModal.vue